import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronRight, Pin, Trash2, Bot, Clock, Folder, FolderOpen, Home } from 'lucide-react';
import { useDocuments } from '../../hooks/useDocuments';
import type { Document } from '../../types';
import { parseUTCTimestamp } from '../../utils/dateUtils';

function formatAge(isoString: string): string {
  const date = parseUTCTimestamp(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays > 0) return `${diffDays}d ago`;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours > 0) return `${diffHours}h ago`;
  return 'just now';
}

export const AgentDeskViewer: React.FC = () => {
  const { documents: allAgentDocs, isLoading, deleteDocument } = useDocuments({ owner: 'agent' });
  // Exclude task-linked docs — those are visible on the task itself
  const standaloneDocs = allAgentDocs.filter((doc: Document) => !doc.task_id);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);

  const handleDelete = async (doc: Document) => {
    if (!confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    setDeletingId(doc.id);
    try {
      await deleteDocument(doc.id);
    } finally {
      setDeletingId(null);
    }
  };

  // Build folder structure from documents
  const docsInCurrentFolder = standaloneDocs.filter((doc: Document) => {
    if (currentFolder === null) return !doc.folder;
    return doc.folder === currentFolder;
  });

  // Collect immediate sub-folders
  const subfolderCounts: Record<string, number> = {};
  standaloneDocs.forEach((doc: Document) => {
    if (!doc.folder) return;

    if (currentFolder === null) {
      // At root: top-level folders
      const topLevel = doc.folder.split('/')[0];
      subfolderCounts[topLevel] = (subfolderCounts[topLevel] || 0) + 1;
    } else if (doc.folder.startsWith(currentFolder + '/')) {
      // In a folder: immediate children
      const remainder = doc.folder.slice(currentFolder.length + 1);
      const childName = remainder.split('/')[0];
      subfolderCounts[childName] = (subfolderCounts[childName] || 0) + 1;
    }
  });
  const subfolders = Object.entries(subfolderCounts).sort(([a], [b]) => a.localeCompare(b));

  // Breadcrumb segments
  const breadcrumbs = currentFolder ? currentFolder.split('/') : [];

  const navigateToFolder = (folderName: string) => {
    if (currentFolder === null) {
      setCurrentFolder(folderName);
    } else {
      setCurrentFolder(currentFolder + '/' + folderName);
    }
    setExpandedId(null);
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      setCurrentFolder(null);
    } else {
      setCurrentFolder(breadcrumbs.slice(0, index + 1).join('/'));
    }
    setExpandedId(null);
  };

  const getPreview = (doc: Document): string => {
    if (doc.summary) return doc.summary;
    return doc.body.slice(0, 120);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Agent&apos;s Desk
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Persistent documents created by the AI agent. Research notes, plans, and reference material.
      </p>

      {/* Breadcrumb navigation */}
      {currentFolder !== null && (
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2 flex-wrap">
          <button
            onClick={() => navigateToBreadcrumb(-1)}
            className="flex items-center gap-0.5 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <Home className="w-3 h-3" />
            Root
          </button>
          {breadcrumbs.map((segment, i) => (
            <React.Fragment key={i}>
              <span className="text-gray-300 dark:text-gray-600">/</span>
              {i < breadcrumbs.length - 1 ? (
                <button
                  onClick={() => navigateToBreadcrumb(i)}
                  className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {segment}
                </button>
              ) : (
                <span className="text-gray-700 dark:text-gray-200 font-medium">{segment}</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center">
          Loading...
        </div>
      ) : standaloneDocs.length === 0 ? (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center italic">
          No agent documents yet. The agent will create documents as it builds knowledge.
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Sub-folders first */}
          {subfolders.map(([name, count]) => (
            <button
              key={`folder-${name}`}
              onClick={() => navigateToFolder(name)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
            >
              <Folder className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">{name}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{count} docs</span>
            </button>
          ))}

          {/* Documents in current folder */}
          {docsInCurrentFolder.map((doc: Document) => (
            <div
              key={doc.id}
              className="border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 overflow-hidden"
            >
              <button
                onClick={() => setExpandedId(expandedId === doc.id ? null : doc.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              >
                {expandedId === doc.id ? (
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {doc.is_pinned && <Pin className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {doc.title}
                    </span>
                  </div>
                  {expandedId !== doc.id && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {getPreview(doc)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  <Clock className="w-3 h-3" />
                  {formatAge(doc.updated_time)}
                </div>
              </button>

              {expandedId === doc.id && (
                <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 py-2 text-xs text-gray-500 dark:text-gray-400">
                    <Bot className="w-3 h-3" />
                    <span>Created by agent</span>
                    {doc.folder && (
                      <span className="flex items-center gap-0.5">
                        <FolderOpen className="w-3 h-3" />
                        {doc.folder}
                      </span>
                    )}
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none text-sm max-h-[300px] overflow-y-auto">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.body}</ReactMarkdown>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 flex justify-end">
                    <button
                      onClick={() => handleDelete(doc)}
                      disabled={deletingId === doc.id}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3" />
                      {deletingId === doc.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {docsInCurrentFolder.length === 0 && subfolders.length === 0 && (
            <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center italic">
              No documents in this folder.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
